import { useMemo } from 'react';
import { UseQueryState } from 'urql';

import { usePdp } from 'hooks/usePdp';
import { ProductCustomizationProvider } from 'hooks/useProductCustomization';
import { Inventory, Score, Variant } from '__generated__/graphql';
import { getSizesFromSizeGroups } from 'utils/products';
import { useInventory } from 'hooks/useInventory';
import { ProductTypeFromPDPQuery } from 'types/product';
import { ProductDetailsPageContent } from 'groq/pages/ProductDetailsPage';
import { ProductDetailsPageTemplateContent } from 'groq/pages/ProductDetailsPageTemplate';
import { sendMixedCartErrorMessage } from 'utils/mixedCartValidations';
import { useSimpleCart } from 'hooks/useSimpleCart';
import { useItemViewEvent } from 'hooks/useItemViewEvent';
import { useSiteConfig } from 'hooks/useSiteConfig';
import { transformListProductToAnalyticsItem } from 'utils/analytics';
import { useGA4Events } from 'hooks/useGA4Events';
import { getGa4Item } from 'utils/ga4Items';

import { PdpSimplifiedLayout } from './PdpSimplifiedLayout';
import { PdpFullLayout } from './PdpFullLayout';
import { PdpSoldoutWithRecommenderLayout } from './PdpSoldoutWithRecommenderLayout';

export type PdpProps = {
  masterProduct?: ProductTypeFromPDPQuery;
  variation?: Variant | null;
  inventory?: UseQueryState<{ inventory: Inventory }>;
  content?:
    | (ProductDetailsPageContent & ProductDetailsPageTemplateContent)
    | null;
  productScore?: Score;
  isAppOnly?: boolean;
  mixedCartNotAllowed?: boolean;
  sizeHasChanged?: boolean;
};

const PageContent = ({
  pageLayout,
  masterProduct,
  variation,
  inventory,
  content,
  productScore,
  isAppOnly,
  mixedCartNotAllowed,
  sizeHasChanged,
}: PdpProps & {
  pageLayout: string;
}) => {
  switch (pageLayout) {
    case 'simplified':
      return (
        <PdpSimplifiedLayout
          masterProduct={masterProduct}
          variation={variation}
          inventory={inventory}
          content={content}
          mixedCartNotAllowed={mixedCartNotAllowed}
          sizeHasChanged={sizeHasChanged}
        />
      );
    case 'soldoutWithRecommender':
      return (
        <PdpSoldoutWithRecommenderLayout
          masterProduct={masterProduct}
          variation={variation}
          content={content}
        />
      );
    case 'noVariant':
      return null;
    case 'full':
    default:
      return (
        <PdpFullLayout
          masterProduct={masterProduct}
          variation={variation}
          inventory={inventory}
          content={content}
          productScore={productScore}
          isAppOnly={isAppOnly}
          mixedCartNotAllowed={mixedCartNotAllowed}
          sizeHasChanged={sizeHasChanged}
        />
      );
  }
};

export const ProductDetailsPage = () => {
  const {
    desiredSize,
    desiredSwatch,
    masterProduct,
    pageLayout,
    variation,
    content,
    productScore,
    isAppOnly,
    sizeHasChanged,
  } = usePdp();
  const { cart } = useSimpleCart();
  const selectedSwatch = masterProduct?.variations.find(
    item => item.colorValue === desiredSwatch
  ) as Variant;

  const mixedCartNotAllowed =
    cart?.isMixedCartAllowed === false &&
    sendMixedCartErrorMessage(cart, variation!);

  const sizes = selectedSwatch
    ? getSizesFromSizeGroups(selectedSwatch?.sizeGroups)
    : [];

  const selectedSize = sizes.find(item => item.id === desiredSize);

  const [inventory] = useInventory(selectedSize?.productId);

  const { currency } = useSiteConfig();

  const { imageNumber, imageType } = useGA4Events();

  const ga4Item =
    masterProduct?.id && variation?.colorValue
      ? getGa4Item(`${masterProduct.id}_${variation.colorValue}`)
      : undefined;

  const item = useMemo(() => {
    const product = {
      color: variation?.colorValue,
      id: variation?.id,
      masterId: masterProduct?.id,
      masterProduct: masterProduct,
      variantProduct: variation,
    };

    return transformListProductToAnalyticsItem({
      product,
      currency: currency.code,
      quantity: 1,
      categories: {
        item_category: masterProduct?.primaryCategoryId,
      },
      itemListId: ga4Item?.lid,
      itemListName: ga4Item?.lname,
      index: ga4Item?.idx,
      creativeName: ga4Item?.cname,
      creativeSlot: ga4Item?.cslot,
      promotionId: ga4Item?.pid,
      promotionName: ga4Item?.pname,
      itemId: variation?.id,
      itemName: variation?.name,
    });
  }, [currency.code, variation, masterProduct, ga4Item]);

  useItemViewEvent({
    itemListId: ga4Item?.lid,
    itemListName: ga4Item?.lname,
    itemIdEp: variation?.id,
    itemNameEp: variation?.name,
    value: variation?.productPrice?.salePrice || undefined,
    imageType,
    imageNumber,
    item: {
      ...item,
      color_code: `a${variation?.colorValue}`,
    },
    currency: currency.code,
    desiredSize,
    selectedSize,
  });

  return (
    <ProductCustomizationProvider variation={variation}>
      <PageContent
        pageLayout={pageLayout}
        masterProduct={masterProduct}
        variation={variation}
        inventory={inventory}
        content={content}
        productScore={productScore}
        isAppOnly={isAppOnly}
        mixedCartNotAllowed={mixedCartNotAllowed}
        sizeHasChanged={sizeHasChanged}
      />
    </ProductCustomizationProvider>
  );
};
